import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "./lib/cn.js";

/**
 * Which rating engine the number represents. Public profile shows both;
 * leaderboards typically pin to one.
 *
 * - `bt`: Bradley-Terry — anchored 1000, ×400 scaling. The public board.
 * - `glicko`: Glicko-2 — matchmaking rating. `provisional` until RD < 110.
 */
export type RatingEngine = "bt" | "glicko";

export interface RatingBadgeProps extends HTMLAttributes<HTMLSpanElement> {
 rating: number;
 /** ±95% CI for the rating estimate. */
 ci?: number;
 /** Δ since the prior recompute. Positive renders as ▲, negative as ▼. */
 delta?: number | null;
 /** Glicko-2 only: first 15 matches before RD drops below threshold. */
 provisional?: boolean;
 /** Adds a quiet leading label, e.g. `BT 1847 ±31`. */
 engine?: RatingEngine;
}

export const RatingBadge = forwardRef<HTMLSpanElement, RatingBadgeProps>(
 ({ rating, ci, delta, provisional, engine, className, ...props }, ref) => {
 const deltaTone =
 delta == null || delta === 0
 ? "var(--pp-text-muted)"
 : delta > 0
 ? "var(--accent)"
 : "var(--pp-danger)";

 return (
 <span
 ref={ref}
 className={cn(
 "inline-flex items-baseline gap-1.5 font-[var(--pp-font-family)] tabular-nums",
 "text-[var(--pp-font-md)] leading-none",
 className,
 )}
 {...props}
 >
 {engine && (
 <span
 style={{
 fontSize: "var(--pp-font-sm)",
 color: "var(--pp-text-muted)",
 textTransform: "uppercase",
 letterSpacing: "var(--pp-tracking-caps)",
 }}
 >
 {engine === "bt" ? "BT" : "Glicko"}
 </span>
 )}
 <span style={{ fontWeight: 600, color: "var(--pp-text)" }}>
 {Math.round(rating)}
 </span>
 {ci !== undefined && (
 <span
 style={{
 color: "var(--pp-text-muted)",
 fontSize: "var(--pp-font-sm)",
 }}
 >
 ±{Math.round(ci)}
 </span>
 )}
 {delta != null && delta !== 0 && (
 <span style={{ color: deltaTone, fontSize: "var(--pp-font-sm)" }}>
 {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
 </span>
 )}
 {provisional && (
 <span
 style={{
 fontSize: "var(--pp-font-sm)",
 color: "var(--pp-text-muted)",
 textTransform: "uppercase",
 letterSpacing: "var(--pp-tracking-caps)",
 }}
 >
 prov
 </span>
 )}
 </span>
 );
 },
);

RatingBadge.displayName = "RatingBadge";
